import { css } from '@emotion/react'
import Link from 'next/link'
import { Card, colors, CompanyLogo } from 'plume-ui'
import { useEffect, useState } from 'react'
import { fetchOverseaBenchmarkCompanies } from '@/lib/api/company'
import { companyDefaultImage } from '@/lib/company'
import { formatLastFundingType } from '@/lib/roundtype'
import { COMPANY_HREF } from '@/lib/url'
import { OverseaBenchmarkMappingData } from '../../../../../types/api'
import { A } from '../../../../atoms/A'
import { IPOIcon } from '../../../../atoms/icons/IPOIcon'
import { LineClampText } from '../../../../atoms/LineClampText'
import {
  Table,
  TableCell,
  TableHead,
  TableHeading,
  TableRow,
} from '../../../../atoms/Table'
import { TotalFundraiseValues } from '../../../../atoms/table/TotalFundraiseValues'
import { TagButton, TagButtonGroup } from '../../../../atoms/TagButton'
import { EmptyCard } from '../../../../common/EmptyCard'
import { Help } from '../../../../common/Help'
import { FormattedDate } from '../../../../atoms/FormattedDate'
import { CardInnerTitle } from '@/components/atoms/CardInnerTitle'
import { LoadingSpinner } from '@/components/atoms/LoadingSpinner'

type Props = {
  shortname: string
  className?: string
}

export const OverseaBenchmarkMappingTable: React.FC<Props> = (props) => {
  const [overseaBenchmarkCompanies, setOverseaBenchmarkCompanies] = useState<
    OverseaBenchmarkMappingData[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    setIsLoading(true)
    setOverseaBenchmarkCompanies([])
    fetchOverseaBenchmarkCompanies(props.shortname).then((res) => {
      setOverseaBenchmarkCompanies(res.overseaBenchmarkCompanies)
      setIsLoading(false)
    })
  }, [props.shortname])

  return (
    <Card data-test-id="oversea-benchmark" className={props.className}>
      <CardInnerTitle title="海外スタートアップ" />
      {isLoading ? (
        <LoadingSpinner size={3} />
      ) : overseaBenchmarkCompanies.length > 0 ? (
        <Table className="oversea-benchmark-companies">
          <TableHead className="header">
            <tr
              css={css`
                > th {
                  background-color: ${colors.white10} !important;
                }
              `}
            >
              <TableHeading className="company-name">企業名</TableHeading>
              <TableHeading className="country-name">国・地域</TableHeading>
              <TableHeading className="last-funding-on" align="center">
                <div>
                  <div>最新ラウンド</div>
                  <div>調達日</div>
                </div>
              </TableHeading>
              <TableHeading className="last-funding-type" align="center">
                <div>
                  <div>最新ラウンド</div>
                  <div>タイプ</div>
                </div>
                <Help
                  className="help"
                  linkTo="https://help.initial.inc/ja/articles/5489432"
                  css={css`
                    vertical-align: bottom;
                  `}
                >
                  定義を見る
                </Help>
              </TableHeading>
              <TableHeading className="total-fundraise" align="center">
                総調達額
              </TableHeading>
              <TableHeading className="valuation" align="center">
                調達後評価額
              </TableHeading>
            </tr>
          </TableHead>
          <tbody>
            {overseaBenchmarkCompanies.map((company, i) => (
              <TableRow highlightOnHover className="row" key={i}>
                <TableCell width="50%">
                  <div
                    css={css`
                      display: flex;
                    `}
                  >
                    <Link
                      as={`/companies/${company.globalId}`}
                      href={COMPANY_HREF}
                      passHref
                    >
                      <A>
                        <CompanyLogo
                          className="company-logo"
                          src={
                            company.logoUrl ||
                            companyDefaultImage(
                              company.listed ? 'IPO企業' : '未公開企業'
                            )
                          }
                          size="normal"
                          name={company.name}
                        />
                      </A>
                    </Link>
                    <div
                      css={css`
                        margin-left: 10px;
                        flex: 1;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                        `}
                      >
                        <div className="company-name">
                          <Link
                            as={`/companies/${company.globalId}`}
                            href={COMPANY_HREF}
                            passHref
                          >
                            <A
                              decoration="none"
                              css={css`
                                font-size: 15px;
                              `}
                            >
                              {company.name}
                            </A>
                          </Link>
                        </div>
                        {company.listed && (
                          <IPOIcon
                            isHiddenTooltip={true}
                            className="ipo-label"
                            css={css`
                              margin-left: 3px;
                            `}
                          />
                        )}
                      </div>
                      <LineClampText
                        justify
                        lineNumber={5}
                        css={css`
                          font-size: 12px;
                          line-height: 1.5;
                        `}
                        stripLineBreaks
                        className="description"
                      >
                        {company.description}
                      </LineClampText>
                      <div
                        css={css`
                          margin-top: 6px;
                        `}
                      >
                        {company.tags && company.tags.length > 0 ? (
                          <TagButtonGroup className="tags">
                            {company.tags.map((tag, i) => (
                              <TagButton key={i} tag={tag} />
                            ))}
                          </TagButtonGroup>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell nowrap align="left" vertical="middle">
                  <div className="country-name">{company.country}</div>
                </TableCell>
                <TableCell vertical="middle" className="last-funding-on">
                  {company.totalFundraise && company.totalFundraise.date ? (
                    <div>
                      <FormattedDate>
                        {company.totalFundraise.date}
                      </FormattedDate>
                    </div>
                  ) : null}
                </TableCell>
                <TableCell vertical="middle">
                  <div className="last-funding-type">
                    {company.lastFundingType &&
                      formatLastFundingType(company.lastFundingType)}
                  </div>
                </TableCell>
                <TableCell vertical="middle" align="right" width="10%">
                  <div className="total-fundraise">
                    {company.totalFundraise &&
                    company.totalFundraise.value !== null &&
                    company.totalFundraise.value !== 0 ? (
                      <TotalFundraiseValues
                        currency={company.totalFundraise.currency}
                        value={company.totalFundraise.value}
                      />
                    ) : null}
                  </div>
                </TableCell>
                <TableCell vertical="middle" align="right" width="10%">
                  <div className="valuation">
                    {company.valuation &&
                    company.valuation !== null &&
                    company.valuation.value !== 0 ? (
                      <>
                        <TotalFundraiseValues
                          currency={company.valuation.currency}
                          value={company.valuation.value}
                        />
                        {company.listed && (
                          <div
                            className="pre-ipo-valuation"
                            css={css`
                              color: #999;
                              font-size: 11px;
                            `}
                          >
                            IPO前最終調達時
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard>データ未作成です</EmptyCard>
      )}
    </Card>
  )
}
